import React, { useEffect, useState } from "react";
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress
} from "@mui/material";
import { useMarketingGet } from "../Campaign/hooks/useCampaign";
import { formatBdt } from "../Campaign/utils/smsParts";
import "../Campaign/Campaign.css";

const CHANNELS = [
  ["sms", "SMS campaigns"],
  ["push", "Push campaigns"],
  ["reminder", "Reminder pushes"],
  ["call", "AI calls"]
];

const profitColor = (n) => (n > 0 ? "success.main" : n < 0 ? "error.main" : "text.primary");

/** All campaigns: cost vs attributed revenue per month and by send hour (plan item 33) */
const MarketingSummary = () => {
  const [months, setMonths] = useState(3);
  const { data, loading, error, fetch } = useMarketingGet("/marketing/summary");

  useEffect(() => { fetch({ months }); }, [months]);

  return (
    <Box className="campaign-container">
      <Box className="campaign-header">
        <Typography variant="h5">Marketing Results</Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Period</InputLabel>
          <Select label="Period" value={months} onChange={(e) => setMonths(e.target.value)}>
            {[1, 3, 6].map((m) => <MenuItem key={m} value={m}>Last {m} month{m > 1 ? "s" : ""}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Each payment is credited once, to the member's most recent message (any channel) within {data?.windowDays ?? 7} days before it.
        These are attributed numbers; each campaign's page has its holdout comparison, which shows the real effect.
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !data && <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>}

      {data && (
        <>
          <Paper className="form-section">
            <Typography variant="h6" gutterBottom>By month</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    {CHANNELS.map(([k, l]) => <TableCell key={k} align="right">{l}<br /><small>sent · cost · revenue</small></TableCell>)}
                    <TableCell align="right">Total cost</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Profit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.byMonth.length === 0 && (
                    <TableRow><TableCell colSpan={8}>Nothing sent in this period yet.</TableCell></TableRow>
                  )}
                  {data.byMonth.map((m) => (
                    <TableRow key={m.month}>
                      <TableCell>{m.month}</TableCell>
                      {CHANNELS.map(([k]) => (
                        <TableCell key={k} align="right">
                          {m[k].sent.toLocaleString()} · {formatBdt(m[k].cost)} · <b>{formatBdt(m[k].revenue)}</b>
                        </TableCell>
                      ))}
                      <TableCell align="right">{formatBdt(m.total.cost)}</TableCell>
                      <TableCell align="right">{formatBdt(m.total.revenue)}</TableCell>
                      <TableCell align="right" sx={{ color: profitColor(m.total.profit), fontWeight: 600 }}>{formatBdt(m.total.profit)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              All payments in the period: {formatBdt(data.totalRevenue)} · not after any message: {formatBdt(data.unattributedRevenue)}.
            </Typography>
          </Paper>

          <Paper className="form-section">
            <Typography variant="h6" gutterBottom>By send hour (Bangladesh time)</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Hour</TableCell>
                    {CHANNELS.map(([k, l]) => <TableCell key={k} align="right">{l}<br /><small>sent · revenue</small></TableCell>)}
                    <TableCell align="right">Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.byHour.filter((h) => CHANNELS.some(([k]) => h[k].sent > 0) || h.total.revenue > 0).map((h) => (
                    <TableRow key={h.hour}>
                      <TableCell>{h.hour}</TableCell>
                      {CHANNELS.map(([k]) => (
                        <TableCell key={k} align="right">{h[k].sent.toLocaleString()} · {formatBdt(h[k].revenue)}</TableCell>
                      ))}
                      <TableCell align="right"><b>{formatBdt(h.total.revenue)}</b></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default MarketingSummary;
